import { Dropdown } from 'react-native-element-dropdown'
import type { DropdownProps } from 'react-native-element-dropdown/lib/typescript/components/Dropdown/model'
import { SegmentAction, SegmentComponent, triggerSegmentAction } from '@/src/services/segment'
import { sardineIdKey, type SardineId } from '@/src/services/Sardine/SardineId'
import { useSardine } from '@/src/services/Sardine'
import { fontFamilies, mesaColorNames } from '@/src/common/theme'
import type { TextStyle, ViewStyle } from 'react-native'
import { inputMinHeight } from '@/src/components/ui/forms/inputs/Input'
import type { ComponentProps } from 'react'

const commonStyle = {
  fontFamily: fontFamilies['PlusJakartaSans-Regular'],
  fontWeight: 400,
  fontSize: 16,
  color: mesaColorNames['Greyscale/600 #505D64'],
} as const satisfies TextStyle satisfies ViewStyle

interface DropdownPropsInterface<T> extends DropdownProps<T> {
  segmentTrack?: boolean
  segmentAction?: SegmentAction | undefined
  segmentComponent?: SegmentComponent | undefined
  /** @see {@link SardineId} */
  sardineId?: SardineId
}

export const DropdownComponent = <T,>({
  segmentTrack = true,
  segmentAction,
  segmentComponent = 'Dropdown',
  onFocus,
  style,
  placeholderStyle,
  sardineId,
  ...props
}: DropdownPropsInterface<T>) => {
  const { trackTextChange, trackFocusChange } = useSardine()

  const _onFocus: ComponentProps<typeof Dropdown>['onFocus'] = (...args) => {
    onFocus?.(...args)

    if (segmentTrack && segmentAction !== undefined) {
      void triggerSegmentAction({
        component: segmentComponent,
        action: segmentAction,
      })
    }
  }

  return (
    <Dropdown
      {...props}
      placeholderStyle={[commonStyle, { marginLeft: 10 }, placeholderStyle]}
      selectedTextStyle={[commonStyle, { marginLeft: 10 }]}
      itemTextStyle={commonStyle}
      onFocus={_onFocus}
      style={[
        commonStyle,
        {
          borderRadius: 8,
          borderStyle: 'solid',
          borderWidth: 1,
          borderTopColor: mesaColorNames['Greyscale/300 #CDD4D5'],
          borderRightColor: mesaColorNames['Greyscale/300 #CDD4D5'],
          borderBottomColor: mesaColorNames['Greyscale/300 #CDD4D5'],
          borderLeftColor: mesaColorNames['Greyscale/300 #CDD4D5'],
          minHeight: inputMinHeight,
        },
        style,
      ]}
      containerStyle={{
        borderRadius: 8,
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: mesaColorNames['Greyscale/300 #CDD4D5'],
        overflow: 'hidden',
      }}
      {...(sardineId
        ? {
            onChangeText(...args) {
              props.onChangeText?.(...args)
              void trackTextChange(sardineId, args[0])
            },
            onFocus(...args) {
              _onFocus(...args)
              void trackFocusChange(sardineId, true)
            },
            onBlur(...args) {
              props.onBlur?.(...args)
              void trackFocusChange(sardineId, false)
            },
            selectedTextProps: {
              dataSet: {
                [sardineIdKey]: sardineId,
                ...props.selectedTextProps?.dataSet,
              },
            },
          }
        : undefined)}
    />
  )
}
